import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useParams } from "react-router-dom";
import {
  AlertTriangle,
  ArrowLeft,
  Building2,
  Calendar,
  ChevronDown,
  ChevronUp,
  ExternalLink,
  Lock,
  Percent,
  RotateCcw,
  ShieldAlert,
  Ticket,
  Trash2,
  UserCircle2,
  Users,
  Wallet,
} from "lucide-react";
import { producersMgmtApi } from "./api";
import type {
  ProducerClubItem,
  ProducerEventItem,
  ProducerStaffItem,
  SetCommissionPayload,
} from "./types";
import { LoadingBlock, ErrorBlock } from "../dashboard/shared/StateBlocks";
import { formatDateTime } from "../applications/shared/format";
import { useToast } from "@/app/providers/Toaster";
import { confirmAction } from "@/app/providers/ConfirmDialog";
import { Can } from "@/lib/permissions/useCan";
import { PERMISSIONS } from "@/lib/permissions/catalog";

/**
 * Pantalla de detalle del productor. Muestra:
 *  - Info de cuenta + flags editables (isBlocked, isProducer).
 *  - Sección Clubs (lista + hard-delete individual con confirmación).
 *  - Sección Eventos (lista + tickets protegidos + hard-delete).
 *  - Sección Staff/RBAC (revoke individual).
 *  - Footer con acciones destructivas: soft-delete, restore, hard-delete
 *    completo con cascade.
 *
 * Las acciones destructivas usan `confirmAction({ promptLabel })` para
 * pedirle al admin que tipee el email/nombre del recurso a borrar
 * (defensa anti-clicks accidentales). El backend valida de nuevo
 * server-side.
 */
export function ProducerDetailScreen() {
  const { id: idParam } = useParams<{ id: string }>();
  const id = Number(idParam);
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { push } = useToast();

  // Toggle "Forzar borrado" en la zona de peligro. Por default OFF.
  // Cuando ON, el endpoint hard-delete acepta tickets vendidos del propio
  // productor como pérdida aceptable (caso QA con tickets ficticios).
  // NO bypasea bloqueos por tickets comprados en eventos ajenos ni por
  // transferencias firmadas (esos siguen siendo data de terceros).
  const [forceDelete, setForceDelete] = useState(false);

  // Qué eventos tienen el panel de "Entradas" abierto (gestión de sold-out).
  const [openTickets, setOpenTickets] = useState<Set<number>>(new Set());
  const toggleTickets = (eventId: number) =>
    setOpenTickets((prev) => {
      const next = new Set(prev);
      next.has(eventId) ? next.delete(eventId) : next.add(eventId);
      return next;
    });

  const query = useQuery({
    queryKey: ["producers-mgmt", "detail", id],
    queryFn: () => producersMgmtApi.get(id),
    enabled: Number.isFinite(id),
  });

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["producers-mgmt", "detail", id] });
    qc.invalidateQueries({ queryKey: ["producers-mgmt", "list"] });
  };

  const update = useMutation({
    mutationFn: (payload: { isBlocked?: boolean; isProducer?: boolean }) =>
      producersMgmtApi.update(id, payload),
    onSuccess: () => {
      push({ kind: "success", message: "Productor actualizado." });
      invalidate();
    },
    onError: (err: any) =>
      push({
        kind: "error",
        message: err?.response?.data?.message || "Error al actualizar.",
      }),
  });

  const softDelete = useMutation({
    mutationFn: () => producersMgmtApi.softDelete(id),
    onSuccess: () => {
      push({ kind: "success", message: "Productor borrado (soft)." });
      invalidate();
    },
    onError: (err: any) =>
      push({ kind: "error", message: err?.response?.data?.message || "Error en soft-delete." }),
  });

  const restore = useMutation({
    mutationFn: () => producersMgmtApi.restore(id),
    onSuccess: () => {
      push({ kind: "success", message: "Productor restaurado." });
      invalidate();
    },
    onError: (err: any) =>
      push({ kind: "error", message: err?.response?.data?.message || "Error al restaurar." }),
  });

  const hardDelete = useMutation({
    mutationFn: ({ confirmEmail, force }: { confirmEmail: string; force: boolean }) =>
      producersMgmtApi.hardDelete(id, confirmEmail, force),
    onSuccess: () => {
      push({ kind: "success", message: "Productor borrado definitivamente." });
      // El productor ya no existe — removemos el detalle del cache (si invalidás,
      // dispara un refetch que va a 404) y solo invalidamos la lista.
      qc.removeQueries({ queryKey: ["producers-mgmt", "detail", id] });
      qc.invalidateQueries({ queryKey: ["producers-mgmt", "list"] });
      navigate("/producers");
    },
    onError: (err: any) =>
      push({
        kind: "error",
        message: err?.response?.data?.message || "Error al borrar definitivamente.",
      }),
  });

  const hardDeleteClub = useMutation({
    mutationFn: ({ clubId, confirmName, force }: { clubId: number; confirmName: string; force: boolean }) =>
      producersMgmtApi.hardDeleteClub(clubId, confirmName, force),
    onSuccess: () => {
      push({ kind: "success", message: "Club borrado definitivamente." });
      invalidate();
    },
    onError: (err: any) =>
      push({
        kind: "error",
        message: err?.response?.data?.message || "Error al borrar club.",
      }),
  });

  const hardDeleteEvent = useMutation({
    mutationFn: ({ eventId, confirmTitle, force }: { eventId: number; confirmTitle: string; force: boolean }) =>
      producersMgmtApi.hardDeleteEvent(eventId, confirmTitle, force),
    onSuccess: () => {
      push({ kind: "success", message: "Evento borrado definitivamente." });
      invalidate();
    },
    onError: (err: any) =>
      push({
        kind: "error",
        message: err?.response?.data?.message || "Error al borrar evento.",
      }),
  });

  const removeStaff = useMutation({
    mutationFn: (contextRoleId: number) => producersMgmtApi.removeStaff(contextRoleId),
    onSuccess: () => {
      push({ kind: "success", message: "Staff removido." });
      invalidate();
    },
    onError: (err: any) =>
      push({
        kind: "error",
        message: err?.response?.data?.message || "Error al remover staff.",
      }),
  });

  const clubCommission = useMutation({
    mutationFn: ({ clubId, payload }: { clubId: number; payload: SetCommissionPayload }) =>
      producersMgmtApi.setClubCommission(clubId, payload),
    onSuccess: (_d, vars) => {
      push({
        kind: "success",
        message: vars.payload.type === null ? "Comisión del club reseteada al default." : "Comisión del club actualizada.",
      });
      invalidate();
    },
    onError: (err: any) =>
      push({ kind: "error", message: err?.response?.data?.error || "Error al setear comisión del club." }),
  });

  const eventCommission = useMutation({
    mutationFn: ({ eventId, payload }: { eventId: number; payload: SetCommissionPayload }) =>
      producersMgmtApi.setEventCommission(eventId, payload),
    onSuccess: (_d, vars) => {
      push({
        kind: "success",
        message: vars.payload.type === null ? "Override del evento removido." : "Comisión del evento actualizada.",
      });
      invalidate();
    },
    onError: (err: any) =>
      push({ kind: "error", message: err?.response?.data?.error || "Error al setear comisión del evento." }),
  });

  const eventSplit = useMutation({
    mutationFn: ({ eventId, enabled }: { eventId: number; enabled: boolean }) =>
      producersMgmtApi.setEventSplitEnabled(eventId, enabled),
    onSuccess: (_d, vars) => {
      push({
        kind: "success",
        message: vars.enabled
          ? "Split de pagos activado para el evento."
          : "Split de pagos desactivado (vuelve a centralizado).",
      });
      invalidate();
    },
    onError: (err: any) =>
      push({
        kind: "error",
        message:
          err?.response?.data?.message ||
          err?.response?.data?.error ||
          "Error al cambiar el Split del evento.",
      }),
  });

  // ── Handlers de confirmación ────────────────────────────────
  const handleToggleBlocked = async (current: boolean) => {
    const ok = await confirmAction({
      title: current ? "Desbloquear productor" : "Bloquear productor",
      message: current
        ? "El productor podrá volver a operar el Hub normalmente."
        : "El productor no podrá acceder al Hub mientras esté bloqueado. Los eventos publicados siguen visibles.",
      confirmLabel: current ? "Desbloquear" : "Bloquear",
      tone: current ? "neutral" : "danger",
    });
    if (ok === true) update.mutate({ isBlocked: !current });
  };

  const handleToggleProducer = async (current: boolean) => {
    const ok = await confirmAction({
      title: current ? "Quitar rol productor" : "Marcar como productor",
      message: current
        ? "El usuario pierde acceso al Hub. Sus clubs y eventos quedan en BD pero el usuario no los podrá gestionar desde el Hub."
        : "El usuario podrá acceder al Hub y crear clubs.",
      confirmLabel: current ? "Quitar rol" : "Marcar productor",
      tone: current ? "danger" : "neutral",
    });
    if (ok === true) update.mutate({ isProducer: !current });
  };

  const handleSoftDelete = async () => {
    const ok = await confirmAction({
      title: "Borrar productor (soft, reversible)",
      message:
        "El productor desaparece de los listados pero la data queda en BD. Se puede restaurar después. Los clubs y eventos NO se borran.",
      confirmLabel: "Borrar (soft)",
      tone: "danger",
    });
    if (ok === true) softDelete.mutate();
  };

  const handleRestore = async () => {
    restore.mutate();
  };

  const handleHardDelete = async (email: string) => {
    const value = await confirmAction({
      title: "Borrar DEFINITIVAMENTE",
      message: `Esta acción elimina al productor, sus clubs, eventos, tickets sin venta y staff/RBAC. NO ES REVERSIBLE. Para confirmar, escribí "${email}" abajo.`,
      promptLabel: `Email del productor (${email})`,
      confirmLabel: "Borrar todo",
      tone: "danger",
    });
    if (value === null || typeof value !== "string") return;
    if (value.trim().toLowerCase() !== email.toLowerCase()) {
      push({ kind: "error", message: "El email no coincide. Operación cancelada." });
      return;
    }
    hardDelete.mutate({ confirmEmail: value, force: forceDelete });
  };

  const handleHardDeleteClub = async (club: ProducerClubItem) => {
    const value = await confirmAction({
      title: "Borrar club DEFINITIVAMENTE",
      message: `Se eliminan los eventos sin tickets pagados, sus staff/RBAC, y el club. Si hay tickets vendidos, el backend bloqueará la acción (o usa "Forzar" si es data de QA). Escribí "${club.name}" para confirmar.`,
      promptLabel: `Nombre del club`,
      confirmLabel: "Borrar club",
      tone: "danger",
    });
    if (value === null || typeof value !== "string") return;
    if (value.trim() !== club.name) {
      push({ kind: "error", message: "El nombre no coincide." });
      return;
    }
    hardDeleteClub.mutate({ clubId: club.id, confirmName: value, force: forceDelete });
  };

  // Abrir el Hub de un evento logueado como uno mismo (super-usuario Nexus).
  // Pedimos un token de handoff y lo canjeamos en la pantalla de bootstrap del
  // Hub, que pisa la sesión que hubiera en hub.mixcore.club. Abrimos la
  // pestaña de inmediato (dentro del gesto del click) para no caer en el
  // bloqueador de popups; recién después le seteamos la URL con el token.
  const handleOpenInHub = async (eventId: number) => {
    const win = window.open("", "_blank");
    try {
      const { token } = await producersMgmtApi.hubHandoffToken();
      const redirect = encodeURIComponent(`/workspace-prod/${eventId}`);
      const url = `https://hub.mixcore.club/auth/bootstrap?token=${token}&redirect=${redirect}`;
      if (win) win.location.href = url;
      else window.location.href = url; // fallback si el popup fue bloqueado
    } catch {
      if (win) win.close();
      push({
        kind: "error",
        message: "No pudimos abrir el Hub. Intentá de nuevo en unos segundos.",
      });
    }
  };

  const handleHardDeleteEvent = async (event: ProducerEventItem) => {
    // Si tiene tickets protegidos y NO está activado el force, advertimos.
    if (event.protectedTickets > 0 && !forceDelete) {
      push({
        kind: "error",
        message: `Este evento tiene ${event.protectedTickets} ticket(s) vendido(s)/usado(s). Activá "Forzar borrado" abajo si es data de QA.`,
      });
      return;
    }
    const value = await confirmAction({
      title: "Borrar evento DEFINITIVAMENTE",
      message: `Se eliminan los tickets sin venta y el evento. Escribí "${event.nombre}" para confirmar.${forceDelete ? ' Modo "Forzar" activado — los tickets vendidos también se borrarán.' : ""}`,
      promptLabel: `Nombre del evento`,
      confirmLabel: "Borrar evento",
      tone: "danger",
    });
    if (value === null || typeof value !== "string") return;
    if (value.trim() !== event.nombre) {
      push({ kind: "error", message: "El nombre no coincide." });
      return;
    }
    hardDeleteEvent.mutate({ eventId: event.id, confirmTitle: value, force: forceDelete });
  };

  const handleRemoveStaff = async (staff: ProducerStaffItem) => {
    const ok = await confirmAction({
      title: "Remover staff del club",
      message: `${staff.userName || staff.userEmail} pierde el rol "${staff.role}" en este club.`,
      confirmLabel: "Remover",
      tone: "danger",
    });
    if (ok === true) removeStaff.mutate(staff.id);
  };

  if (query.isLoading) return <LoadingBlock label="Cargando productor…" />;
  if (query.isError) return <ErrorBlock onRetry={() => query.refetch()} />;
  if (!query.data) return null;

  const { user, clubs, events, staff, warnings, mixcoreDefaultCommissionPct, producerHasMp } = query.data;
  const fullName =
    [user.firstName, user.lastName].filter(Boolean).join(" ") ||
    user.userName ||
    user.email;
  const isDeleted = !!user.deletedAt;

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <button
          onClick={() => navigate("/producers")}
          className="nx-btn-ghost inline-flex items-center gap-1.5 text-sm"
        >
          <ArrowLeft size={14} /> Volver
        </button>
      </div>

      {/* Identidad */}
      <div className="nx-card p-5">
        <div className="flex items-start gap-4">
          {user.img ? (
            <img
              src={user.img}
              alt=""
              className="w-16 h-16 rounded-full object-cover border border-border"
            />
          ) : (
            <div className="w-16 h-16 rounded-full bg-bg-subtle border border-border flex items-center justify-center text-fg-muted">
              <UserCircle2 size={28} />
            </div>
          )}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="nx-h1">{fullName}</h1>
              {isDeleted && (
                <span className="nx-badge bg-danger-soft text-danger">Borrado (soft)</span>
              )}
              {user.isBlocked && (
                <span className="nx-badge bg-danger-soft text-danger">Bloqueado</span>
              )}
            </div>
            <div className="text-sm text-fg-muted mt-1">{user.email}</div>
            <div className="text-xs text-fg-subtle mt-2">
              ID #{user.id} · Registrado {formatDateTime(user.createdAt)}
              {user.city && <> · {user.city}, {user.region}, {user.country}</>}
            </div>
            {(user.isArtist || user.isPartner) && (
              <div className="mt-2 flex gap-1.5 flex-wrap">
                {user.isArtist && (
                  <span className="nx-badge bg-accent-soft text-accent">
                    Artista (id #{user.linkedArtistaId})
                  </span>
                )}
                {user.isPartner && (
                  <span className="nx-badge bg-accent-soft text-accent">
                    Partner (SP #{user.linkedServiceProviderId})
                  </span>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Flags editables */}
      <Can permission={PERMISSIONS.PRODUCERS_MGMT_EDIT}>
        <div className="nx-card p-5">
          <h2 className="nx-h2 mb-3">Flags</h2>
          <div className="grid sm:grid-cols-2 gap-3">
            <FlagButton
              label="Bloqueado"
              description="Bloquea el acceso al Hub."
              active={user.isBlocked}
              onClick={() => handleToggleBlocked(user.isBlocked)}
              activeColor="danger"
            />
            <FlagButton
              label="Es productor"
              description="Acceso al Hub habilitado."
              active={user.isProducer}
              onClick={() => handleToggleProducer(user.isProducer)}
              activeColor="success"
            />
          </div>
        </div>
      </Can>

      {/* Comisión Mixcore (rebaja negociada) */}
      <div className="nx-card p-5">
        <h2 className="nx-h2 inline-flex items-center gap-2 mb-1">
          <Percent size={18} className="text-fg-muted" /> Comisión Mixcore
        </h2>
        <p className="text-sm text-fg-muted mb-4">
          Default de plataforma: <strong>{mixcoreDefaultCommissionPct}%</strong>. La
          tasa por <strong>club</strong> aplica a todos sus eventos (presentes y
          futuros); un <strong>override por evento</strong> la pisa puntualmente.
          La rebaja abarata el precio que paga el comprador.
        </p>

        {/* Por club */}
        <h3 className="text-xs font-semibold uppercase tracking-wide text-fg-subtle mb-2">
          Tasa por club
        </h3>
        {clubs.length === 0 ? (
          <p className="text-sm text-fg-subtle mb-4">Sin clubs.</p>
        ) : (
          <ul className="divide-y divide-border mb-5">
            {clubs.filter((c) => !c.deletedAt).map((c) => {
              const hasRate = c.mixcoreCommissionType === "percent" && c.mixcoreCommissionValue != null;
              return (
                <li key={c.id} className="py-3 flex items-center justify-between gap-3 flex-wrap">
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-fg truncate">
                      {c.name} <span className="text-xs text-fg-subtle font-normal">#{c.id}</span>
                    </div>
                    <div className="text-xs mt-0.5">
                      {hasRate ? (
                        <span className="text-accent font-medium">Tasa negociada: {c.mixcoreCommissionValue}%</span>
                      ) : (
                        <span className="text-fg-subtle">Hereda el default ({mixcoreDefaultCommissionPct}%)</span>
                      )}
                    </div>
                  </div>
                  <Can permission={PERMISSIONS.PRODUCERS_MGMT_EDIT}>
                    <CommissionEditor
                      key={`club-${c.id}-${c.mixcoreCommissionType}-${c.mixcoreCommissionValue}`}
                      currentPct={hasRate ? Number(c.mixcoreCommissionValue) : null}
                      placeholder={String(mixcoreDefaultCommissionPct)}
                      busy={clubCommission.isPending}
                      onApply={(value) => clubCommission.mutate({ clubId: c.id, payload: { type: "percent", value } })}
                      onReset={() => clubCommission.mutate({ clubId: c.id, payload: { type: null, value: null } })}
                    />
                  </Can>
                </li>
              );
            })}
          </ul>
        )}

        {/* Por evento (override puntual) */}
        <h3 className="text-xs font-semibold uppercase tracking-wide text-fg-subtle mb-2">
          Override por evento
        </h3>
        {events.filter((e) => !e.deletedAt).length === 0 ? (
          <p className="text-sm text-fg-subtle">Sin eventos.</p>
        ) : (
          <ul className="divide-y divide-border">
            {events.filter((e) => !e.deletedAt).map((e) => {
              const hasOverride = e.mixcoreCommissionType === "percent" && e.mixcoreCommissionValue != null;
              return (
                <li key={e.id} className="py-3 flex items-center justify-between gap-3 flex-wrap">
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-fg truncate">
                      {e.nombre} <span className="text-xs text-fg-subtle font-normal">#{e.id}</span>
                    </div>
                    <div className="text-xs mt-0.5">
                      <span className="text-fg">Efectiva: {e.effectiveCommissionPct}%</span>{" "}
                      {hasOverride ? (
                        <span className="text-accent">· override del evento</span>
                      ) : (
                        <span className="text-fg-subtle">· heredada (club/default)</span>
                      )}
                    </div>
                  </div>
                  <Can permission={PERMISSIONS.PRODUCERS_MGMT_EDIT}>
                    <CommissionEditor
                      key={`event-${e.id}-${e.mixcoreCommissionType}-${e.mixcoreCommissionValue}`}
                      currentPct={hasOverride ? Number(e.mixcoreCommissionValue) : null}
                      placeholder={String(e.effectiveCommissionPct)}
                      busy={eventCommission.isPending}
                      onApply={(value) => eventCommission.mutate({ eventId: e.id, payload: { type: "percent", value } })}
                      onReset={() => eventCommission.mutate({ eventId: e.id, payload: { type: null, value: null } })}
                    />
                  </Can>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {/* Split de pagos por evento */}
      <div className="nx-card p-5">
        <h2 className="nx-h2 inline-flex items-center gap-2 mb-1">
          <Wallet size={18} className="text-fg-muted" /> Split de pagos
        </h2>
        <p className="text-sm text-fg-subtle mb-3">
          Con Split <span className="text-fg font-medium">activado</span>, el checkout del evento cobra
          directo a la cuenta MercadoPago del organizador y Mixcore retiene su comisión automáticamente
          (el comprador cubre el fee de MP). Sin Split, el evento cobra a la cuenta central de Mixcore
          y el productor se liquida manualmente.
        </p>
        {!producerHasMp && (
          <div className="mb-3 rounded-md border border-warning/40 bg-warning/10 px-3 py-2 text-xs text-warning inline-flex items-start gap-2">
            <AlertTriangle size={14} className="mt-0.5 shrink-0" />
            <span>
              El productor todavía no conectó su MercadoPago, así que no se puede activar el Split. Debe
              hacerlo desde su perfil (mixcore.club → Mi Perfil → Conectar con Mercado Pago).
            </span>
          </div>
        )}
        {events.filter((e) => !e.deletedAt).length === 0 ? (
          <p className="text-sm text-fg-subtle">Sin eventos.</p>
        ) : (
          <ul className="divide-y divide-border">
            {events.filter((e) => !e.deletedAt).map((e) => (
              <li key={e.id} className="py-3 flex items-center justify-between gap-3 flex-wrap">
                <div className="min-w-0">
                  <div className="text-sm font-medium text-fg truncate">
                    {e.nombre} <span className="text-xs text-fg-subtle font-normal">#{e.id}</span>
                  </div>
                  <div className="text-xs mt-0.5">
                    {e.paymentSplitEnabled ? (
                      <span className="text-accent">· Split ACTIVADO — cobra directo al productor</span>
                    ) : (
                      <span className="text-fg-subtle">· Centralizado — cuenta Mixcore + liquidación</span>
                    )}
                  </div>
                </div>
                <Can permission={PERMISSIONS.PRODUCERS_MGMT_EDIT}>
                  <button
                    disabled={!producerHasMp || eventSplit.isPending}
                    onClick={() => eventSplit.mutate({ eventId: e.id, enabled: !e.paymentSplitEnabled })}
                    className={
                      (e.paymentSplitEnabled ? "nx-btn-danger-ghost" : "nx-btn-primary") +
                      " inline-flex items-center gap-1.5 text-xs disabled:opacity-50 disabled:cursor-not-allowed"
                    }
                    title={
                      !producerHasMp
                        ? "El productor debe conectar su MercadoPago primero"
                        : e.paymentSplitEnabled
                          ? "Desactivar Split (volver a centralizado)"
                          : "Activar Split para este evento"
                    }
                  >
                    <Wallet size={12} />
                    {e.paymentSplitEnabled ? "Desactivar Split" : "Activar Split"}
                  </button>
                </Can>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Clubs */}
      <div className="nx-card p-5">
        <div className="flex items-center justify-between mb-3">
          <h2 className="nx-h2 inline-flex items-center gap-2">
            <Building2 size={18} className="text-fg-muted" /> Clubs ({clubs.length})
          </h2>
        </div>
        {clubs.length === 0 ? (
          <p className="text-sm text-fg-subtle">Sin clubs.</p>
        ) : (
          <ul className="divide-y divide-border">
            {clubs.map((c) => (
              <li key={c.id} className="py-2.5 flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <div className="text-sm font-medium text-fg truncate">
                    {c.name}{" "}
                    <span className="text-xs text-fg-subtle font-normal">#{c.id}</span>
                  </div>
                  {c.deletedAt && (
                    <div className="text-xs text-danger mt-0.5">
                      Borrado: {formatDateTime(c.deletedAt)}
                    </div>
                  )}
                </div>
                <Can permission={PERMISSIONS.PRODUCERS_MGMT_HARD_DELETE}>
                  <button
                    onClick={() => handleHardDeleteClub(c)}
                    className="nx-btn-danger-ghost inline-flex items-center gap-1.5 text-xs"
                    title="Borrar club definitivamente"
                  >
                    <Trash2 size={12} /> Borrar
                  </button>
                </Can>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Eventos */}
      <div className="nx-card p-5">
        <h2 className="nx-h2 inline-flex items-center gap-2 mb-3">
          <Calendar size={18} className="text-fg-muted" /> Eventos ({events.length})
        </h2>
        {events.length === 0 ? (
          <p className="text-sm text-fg-subtle">Sin eventos.</p>
        ) : (
          <ul className="divide-y divide-border">
            {events.map((e) => {
              const isOpen = openTickets.has(e.id);
              return (
                <li key={e.id} className="py-2.5">
                  <div className="flex items-center justify-between gap-3">
                    <div className="min-w-0">
                      <div className="text-sm font-medium text-fg truncate">
                        {e.nombre}{" "}
                        <span className="text-xs text-fg-subtle font-normal">#{e.id}</span>
                      </div>
                      <div className="text-xs text-fg-subtle mt-0.5">
                        Fecha: {e.fecha}
                        {e.protectedTickets > 0 && (
                          <span className="ml-2 text-warning inline-flex items-center gap-1">
                            <Lock size={10} /> {e.protectedTickets} tickets protegidos
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <button
                        onClick={() => handleOpenInHub(e.id)}
                        className="nx-btn-ghost inline-flex items-center gap-1.5 text-xs"
                        title="Abrir el Hub de este evento logueado con tu cuenta Nexus (super-usuario)"
                      >
                        <ExternalLink size={12} /> Abrir en Hub
                      </button>
                      <button
                        onClick={() => toggleTickets(e.id)}
                        className="nx-btn-ghost inline-flex items-center gap-1.5 text-xs"
                        title="Gestionar entradas (marcar agotado)"
                      >
                        <Ticket size={12} /> Entradas{" "}
                        {isOpen ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
                      </button>
                      <Can permission={PERMISSIONS.PRODUCERS_MGMT_HARD_DELETE}>
                        <button
                          onClick={() => handleHardDeleteEvent(e)}
                          disabled={e.protectedTickets > 0 && !forceDelete}
                          className="nx-btn-danger-ghost inline-flex items-center gap-1.5 text-xs disabled:opacity-40 disabled:cursor-not-allowed"
                          title={
                            e.protectedTickets > 0 && !forceDelete
                              ? "Tickets vendidos — activá 'Forzar borrado' abajo si es QA"
                              : "Borrar evento definitivamente"
                          }
                        >
                          <Trash2 size={12} />{" "}
                          {forceDelete && e.protectedTickets > 0 ? "Borrar (forzado)" : "Borrar"}
                        </button>
                      </Can>
                    </div>
                  </div>
                  {isOpen && <EventTicketsPanel eventId={e.id} />}
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {/* Staff/RBAC */}
      <div className="nx-card p-5">
        <h2 className="nx-h2 inline-flex items-center gap-2 mb-3">
          <Users size={18} className="text-fg-muted" /> Staff y RBAC ({staff.length})
        </h2>
        {staff.length === 0 ? (
          <p className="text-sm text-fg-subtle">Sin staff asignado.</p>
        ) : (
          <ul className="divide-y divide-border">
            {staff.map((s) => (
              <li key={s.id} className="py-2.5 flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <div className="text-sm font-medium text-fg truncate">
                    {s.userName || s.userEmail || `Usuario #${s.userId}`}
                  </div>
                  <div className="text-xs text-fg-subtle">
                    Club #{s.clubId} · Rol: <span className="text-fg">{s.role}</span>
                    {!s.isActive && <span className="ml-2 text-danger">(inactivo)</span>}
                  </div>
                </div>
                <Can permission={PERMISSIONS.PRODUCERS_MGMT_MANAGE_STAFF}>
                  <button
                    onClick={() => handleRemoveStaff(s)}
                    className="nx-btn-danger-ghost inline-flex items-center gap-1.5 text-xs"
                  >
                    <Trash2 size={12} /> Remover
                  </button>
                </Can>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Zona de peligro */}
      <div className="nx-card p-5 border-danger/30 bg-danger-soft/20">
        <h2 className="nx-h2 inline-flex items-center gap-2 mb-2 text-danger">
          <ShieldAlert size={18} /> Zona de peligro
        </h2>
        <p className="text-sm text-fg-muted mb-4">
          Acciones reversibles e irreversibles sobre toda la cuenta. Las
          irreversibles requieren typing del email y bloquean si hay
          tickets vendidos en sus eventos.
        </p>

        {warnings.protectedTicketsCount > 0 && (
          <div className="mb-4 p-3 rounded-md bg-warning-soft text-warning text-sm inline-flex items-start gap-2">
            <AlertTriangle size={14} className="mt-0.5 flex-shrink-0" />
            <span>{warnings.reason}</span>
          </div>
        )}

        {/* Toggle "Forzar borrado" — solo visible si hay tickets bloqueantes
            propios del productor. NO aparece si el bloqueo es por tickets
            comprados en eventos ajenos o transferencias firmadas (esos NO
            son bypaseables — son data de terceros). */}
        {warnings.protectedTicketsCount > 0 && (
          <Can permission={PERMISSIONS.PRODUCERS_MGMT_HARD_DELETE}>
            <label className="mb-4 flex items-start gap-2 p-3 rounded-md border border-danger/30 bg-bg-card cursor-pointer">
              <input
                type="checkbox"
                checked={forceDelete}
                onChange={(e) => setForceDelete(e.target.checked)}
                className="mt-0.5"
              />
              <span className="text-sm">
                <strong className="text-danger">Forzar borrado</strong>{" "}
                <span className="text-fg-muted">
                  — acepto perder {warnings.protectedTicketsCount} ticket(s)
                  vendido(s)/usado(s). Usar SOLO si esta cuenta es de QA y
                  los tickets son ficticios. Queda registrado en audit log.
                </span>
              </span>
            </label>
          </Can>
        )}

        <div className="flex flex-wrap gap-2">
          {!isDeleted && (
            <Can permission={PERMISSIONS.PRODUCERS_MGMT_SOFT_DELETE}>
              <button
                onClick={handleSoftDelete}
                className="nx-btn-secondary inline-flex items-center gap-1.5 text-sm"
              >
                <Trash2 size={14} /> Borrar (soft, reversible)
              </button>
            </Can>
          )}

          {isDeleted && (
            <Can permission={PERMISSIONS.PRODUCERS_MGMT_SOFT_DELETE}>
              <button
                onClick={handleRestore}
                className="nx-btn-primary inline-flex items-center gap-1.5 text-sm"
              >
                <RotateCcw size={14} /> Restaurar
              </button>
            </Can>
          )}

          <Can permission={PERMISSIONS.PRODUCERS_MGMT_HARD_DELETE}>
            <button
              onClick={() => handleHardDelete(user.email)}
              disabled={warnings.protectedTicketsCount > 0 && !forceDelete}
              className="nx-btn-danger inline-flex items-center gap-1.5 text-sm disabled:opacity-40 disabled:cursor-not-allowed"
              title={
                warnings.protectedTicketsCount > 0 && !forceDelete
                  ? "Activá 'Forzar borrado' arriba para habilitar"
                  : ""
              }
            >
              <Trash2 size={14} />{" "}
              {forceDelete && warnings.protectedTicketsCount > 0
                ? "Borrar DEFINITIVAMENTE (forzado)"
                : "Borrar DEFINITIVAMENTE"}
            </button>
          </Can>
        </div>
      </div>
    </div>
  );
}

function FlagButton({
  label,
  description,
  active,
  onClick,
  activeColor,
}: {
  label: string;
  description: string;
  active: boolean;
  onClick: () => void;
  activeColor: "danger" | "warning" | "success";
}) {
  const colorClass =
    activeColor === "danger"
      ? "border-danger/40 bg-danger-soft text-danger"
      : activeColor === "warning"
        ? "border-warning/40 bg-warning-soft text-warning"
        : "border-success/40 bg-success-soft text-success";

  return (
    <button
      onClick={onClick}
      className={`text-left p-3 rounded-md border transition-colors ${
        active
          ? colorClass
          : "border-border bg-bg-card hover:bg-bg-hover text-fg-muted"
      }`}
    >
      <div className="text-sm font-medium mb-0.5">{label}</div>
      <div className="text-xs opacity-80">{description}</div>
      <div className="text-xs mt-1 font-mono">{active ? "ON" : "OFF"}</div>
    </button>
  );
}

/**
 * Editor inline de tasa de comisión (%). "Aplicar" setea un override
 * 'percent'; "Resetear" lo limpia (vuelve al nivel superior de la cascada).
 * Se monta con `key` ligado al valor actual para re-inicializar el input
 * tras cada mutación exitosa.
 */
function CommissionEditor({
  currentPct,
  placeholder,
  busy,
  onApply,
  onReset,
}: {
  currentPct: number | null;
  placeholder: string;
  busy: boolean;
  onApply: (value: number) => void;
  onReset: () => void;
}) {
  const [val, setVal] = useState<string>(currentPct != null ? String(currentPct) : "");
  const num = Number(val);
  const valid = val.trim() !== "" && Number.isFinite(num) && num > 0 && num <= 100;
  const canApply = valid && !busy && num !== currentPct;
  return (
    <div className="flex items-center gap-1.5">
      <div className="flex items-center">
        <input
          type="number"
          inputMode="decimal"
          min={0}
          max={100}
          step={0.1}
          value={val}
          onChange={(e) => setVal(e.target.value)}
          placeholder={placeholder}
          className="w-16 px-2 py-1 text-sm rounded-md border border-border bg-bg-card text-fg text-right focus:outline-none focus:ring-1 focus:ring-accent"
        />
        <span className="text-xs text-fg-muted ml-1">%</span>
      </div>
      <button
        onClick={() => valid && onApply(num)}
        disabled={!canApply}
        className="nx-btn-primary text-xs disabled:opacity-40 disabled:cursor-not-allowed"
      >
        Aplicar
      </button>
      {currentPct != null && (
        <button
          onClick={onReset}
          disabled={busy}
          className="nx-btn-ghost text-xs"
          title="Volver al default / heredado"
        >
          Resetear
        </button>
      )}
    </div>
  );
}

/**
 * Panel expandible con las entradas (tipos de ticket) de un evento. Permite
 * marcar/desmarcar cada una como AGOTADA desde Nexus (espejo del toggle del
 * Hub). Carga lazy al abrirse; cada toggle queda en el audit log del backend.
 */
function EventTicketsPanel({ eventId }: { eventId: number }) {
  const qc = useQueryClient();
  const { push } = useToast();

  const query = useQuery({
    queryKey: ["producers-mgmt", "ticket-types", eventId],
    queryFn: () => producersMgmtApi.getEventTicketTypes(eventId),
  });

  const toggle = useMutation({
    mutationFn: ({ id, soldOut }: { id: number; soldOut: boolean }) =>
      producersMgmtApi.setTicketTypeSoldOut(id, soldOut),
    onSuccess: (_d, vars) => {
      push({
        kind: "success",
        message: vars.soldOut ? "Entrada marcada como agotada." : "Entrada reabierta.",
      });
      qc.invalidateQueries({ queryKey: ["producers-mgmt", "ticket-types", eventId] });
    },
    onError: (err: any) =>
      push({ kind: "error", message: err?.response?.data?.error || "Error al actualizar la entrada." }),
  });

  const fmt = (n: number) => `$${Number(n || 0).toLocaleString("es-CL")}`;

  return (
    <div className="mt-3 ml-1 pl-3 border-l-2 border-border">
      {query.isLoading ? (
        <p className="text-xs text-fg-subtle py-2">Cargando entradas…</p>
      ) : query.isError ? (
        <p className="text-xs text-danger py-2">Error al cargar las entradas.</p>
      ) : !query.data || query.data.ticketTypes.length === 0 ? (
        <p className="text-xs text-fg-subtle py-2">Este evento no tiene entradas.</p>
      ) : (
        <ul className="divide-y divide-border">
          {query.data.ticketTypes.map((t) => (
            <li key={t.id} className="py-2 flex items-center justify-between gap-3 flex-wrap">
              <div className="min-w-0">
                <div className="text-sm text-fg">
                  {t.nombre}{" "}
                  <span className="text-xs text-fg-subtle">#{t.id}</span>
                  {t.manualSoldOut && (
                    <span className="nx-badge bg-danger-soft text-danger ml-2">Agotada</span>
                  )}
                </div>
                <div className="text-xs text-fg-subtle mt-0.5">
                  {fmt(t.precio)} · stock {t.cantidad} · vendidas {t.vendidos}
                </div>
              </div>
              <Can permission={PERMISSIONS.PRODUCERS_MGMT_MANAGE_EVENTS}>
                <button
                  onClick={() => toggle.mutate({ id: t.id, soldOut: !t.manualSoldOut })}
                  disabled={toggle.isPending}
                  className={
                    (t.manualSoldOut ? "nx-btn-primary" : "nx-btn-secondary") +
                    " text-xs disabled:opacity-40"
                  }
                  title={t.manualSoldOut ? "Reabrir la venta" : "Marcar como agotada"}
                >
                  {t.manualSoldOut ? "Reabrir" : "Marcar agotada"}
                </button>
              </Can>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
